
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

export interface IDonutChartSlice {
  key: string | number;
  label: string;
  sublabel?: string | null;
  value: number;
  color: string;
}

interface IArc {
  slice: IDonutChartSlice;
  path: string;
}

const CENTER = 100;
const OUTER_RADIUS = 96;
const INNER_RADIUS = 62;

/**
 * Part-to-whole donut with a ranked legend that carries every name, value and share
 * (identity is never color alone). Hovering a slice or a legend row shows it in the center.
 */
@Component({
  name: 'DonutChart',
})
export default class DonutChart extends Vue {
  @Prop({ required: true })
  protected slices!: IDonutChartSlice[];

  @Prop({ default: '' })
  protected centerCaption!: string;

  @Prop({ default: '' })
  protected emptyLabel!: string;

  // The selected slice: the others are dimmed.
  @Prop({ default: null })
  protected activeKey!: string | number | null;

  protected hovered: number | null = null;

  get total(): number {
    return this.slices.reduce((sum, slice) => sum + slice.value, 0);
  }

  get hoveredSlice(): IDonutChartSlice | null {
    return this.hovered === null ? null : this.slices[this.hovered] || null;
  }

  get ariaLabel(): string {
    return this.slices.map(slice => slice.label + ' ' + this.percentOf(slice.value)).join(', ');
  }

  get arcs(): IArc[] {
    let start = 0;

    return this.slices.map(slice => {
      const sweep = this.total ? (slice.value / this.total) * 360 : 0;
      const arc = { slice, path: this.annularSector(start, start + sweep) };
      start += sweep;

      return arc;
    });
  }

  isDimmed(index: number): boolean {
    const slice = this.slices[index];

    return this.activeKey !== null && !!slice && slice.key !== this.activeKey;
  }

  formatNumber(value: number): string {
    return value.toLocaleString(this.$i18n.locale);
  }

  percentOf(value: number): string {
    if (!this.total) {
      return '0%';
    }
    const percent = (value / this.total) * 100;

    return (percent < 10 ? percent.toFixed(1) : Math.round(percent).toString()) + '%';
  }

  // A ring segment from startAngle to endAngle (degrees, clockwise from 12 o'clock).
  private annularSector(startAngle: number, endAngle: number): string {
    // a full ring cannot be one arc: draw it as two halves
    if (endAngle - startAngle >= 359.99) {
      return this.annularSector(0, 180) + ' ' + this.annularSector(180, 360);
    }
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    const outerStart = this.point(OUTER_RADIUS, startAngle);
    const outerEnd = this.point(OUTER_RADIUS, endAngle);
    const innerEnd = this.point(INNER_RADIUS, endAngle);
    const innerStart = this.point(INNER_RADIUS, startAngle);

    return [
      'M', outerStart.x, outerStart.y,
      'A', OUTER_RADIUS, OUTER_RADIUS, 0, largeArc, 1, outerEnd.x, outerEnd.y,
      'L', innerEnd.x, innerEnd.y,
      'A', INNER_RADIUS, INNER_RADIUS, 0, largeArc, 0, innerStart.x, innerStart.y,
      'Z',
    ].join(' ');
  }

  private point(radius: number, angle: number): { x: number; y: number } {
    const radians = ((angle - 90) * Math.PI) / 180;

    return {
      x: +(CENTER + radius * Math.cos(radians)).toFixed(3),
      y: +(CENTER + radius * Math.sin(radians)).toFixed(3),
    };
  }
}
